import React from "react";
import { Card, Descriptions, Table, Tag, Typography } from "antd";
import { useModules } from "../../context/ModuleContext";

const { Text } = Typography;

const SEVERITY_COLOR = { MILD: "green", MODERATE: "orange", SEVERE: "red", CRITICAL: "volcano" };
const EMS_COLOR = {
  PENDING: "orange", DISPATCHED: "blue", IN_TRANSIT: "purple",
  COMPLETED: "green", CANCELLED: "default",
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

export default function PatientHistoryView({ data }) {
  const { hasModule } = useModules();

  if (!data) return <Text type="secondary">No data available.</Text>;

  const diagnoses     = (data.diagnoses     ?? []).map((d, i) => ({ ...d, key: d.id ?? i }));
  const prescriptions = (data.prescriptions ?? []).map((p, i) => ({ ...p, key: p.id ?? i }));
  const emsRequests   = (data.ems_requests  ?? []).map((e, i) => ({ ...e, key: e.id ?? i }));
  const eyeExams      = (data.eye_examinations ?? []).map((e, i) => ({ ...e, key: e.id ?? i }));

  return (
    <>
      <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Patient Email">{data.patient_email ?? "—"}</Descriptions.Item>
        <Descriptions.Item label="Patient PIN">
          {data.patient_pin
            ? <Text code style={{ fontWeight: 600 }}>{data.patient_pin}</Text>
            : "—"}
        </Descriptions.Item>
      </Descriptions>

      <Card size="small" title={<Text strong>Diagnoses ({diagnoses.length})</Text>}
        style={{ marginBottom: 12, borderRadius: 6 }}>
        <Table
          size="small"
          pagination={false}
          dataSource={diagnoses}
          columns={[
            { title: "Date",      dataIndex: "diagnosis_date",    key: "diagnosis_date",    width: 110, render: fmtDate },
            { title: "ICD-10",    dataIndex: "icd10_code",        key: "icd10_code",        width: 90 },
            { title: "Condition", dataIndex: "icd10_description", key: "icd10_description", ellipsis: true },
            { title: "Severity",  dataIndex: "severity",          key: "severity",          width: 100,
              render: s => s ? <Tag color={SEVERITY_COLOR[s] ?? "default"}>{s}</Tag> : "—" },
          ]}
          locale={{ emptyText: "No diagnoses recorded for this patient yet. Add a diagnosis via the Diagnoses section." }}
        />
      </Card>

      <Card size="small" title={<Text strong>Prescriptions ({prescriptions.length})</Text>}
        style={{ marginBottom: 12, borderRadius: 6 }}>
        <Table
          size="small"
          pagination={false}
          dataSource={prescriptions}
          columns={[
            { title: "Date",       dataIndex: "created_at",      key: "created_at",      width: 110, render: fmtDate },
            { title: "Medication", dataIndex: "medication_name", key: "medication_name", ellipsis: true },
            { title: "Dosage",     dataIndex: "dosage",          key: "dosage",          width: 110 },
            { title: "Status",     dataIndex: "is_dispensed",    key: "is_dispensed",    width: 110,
              render: v => v ? <Tag color="green">Dispensed</Tag> : <Tag color="orange">Pending</Tag> },
          ]}
          locale={{ emptyText: "No prescriptions issued for this patient yet." }}
        />
      </Card>

      {hasModule("EMS") && (
        <Card size="small" title={<Text strong>EMS Requests ({emsRequests.length})</Text>}
          style={{ marginBottom: 12, borderRadius: 6 }}>
          <Table
            size="small"
            pagination={false}
            dataSource={emsRequests}
            columns={[
              { title: "Requested", dataIndex: "requested_at", key: "requested_at", width: 140, render: fmtDate },
              { title: "Status",    dataIndex: "status",       key: "status",       width: 120,
                render: s => <Tag color={EMS_COLOR[s] ?? "default"}>{s ?? "—"}</Tag> },
            ]}
            locale={{ emptyText: "No EMS requests on file for this patient." }}
          />
        </Card>
      )}

      {hasModule("OPHTHALMOLOGY") && (
        <Card size="small" title={<Text strong>Eye Examinations ({eyeExams.length})</Text>}
          style={{ borderRadius: 6 }}>
          <Table
            size="small"
            pagination={false}
            dataSource={eyeExams}
            columns={[
              { title: "Examination Date", dataIndex: "examination_date", key: "examination_date", width: 160, render: fmtDate },
            ]}
            locale={{ emptyText: "No eye examinations on file for this patient." }}
          />
        </Card>
      )}
    </>
  );
}
